import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { memories } from '../api/client'
import { PageLayout, Card } from '../components/ui'
import PageHeader from '../components/PageHeader'

// Memory type colors and labels
const MEMORY_TYPES = {
  medical: { label: 'Medical', color: 'text-red-400 dark:text-red-400' },
  preference: { label: 'Preference', color: 'text-blue-400 dark:text-blue-400' },
  personal: { label: 'Personal', color: 'text-purple-400 dark:text-purple-400' },
  goal: { label: 'Goal', color: 'text-green-400 dark:text-lume-teal' },
  behavioral: { label: 'Behavioral', color: 'text-yellow-400 dark:text-yellow-400' },
  situational: { label: 'Situational', color: 'text-gray-400 dark:text-indices-muted' },
}

function formatTimeAgo(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function EditMemoryModal({ memory, onClose, onSave }) {
  const [content, setContent] = useState(memory.content)
  const [context, setContext] = useState(memory.context || '')
  const [memoryType, setMemoryType] = useState(memory.memoryType)
  const [isCritical, setIsCritical] = useState(memory.isCritical)
  const [saving, setSaving] = useState(false)

  const [error, setError] = useState(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave({
        id: memory.id,
        content,
        context: context || null,
        memoryType,
        isCritical,
      })
      onClose()
    } catch (err) {
      setError(err?.message || 'Failed to save memory')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-dial-surface border border-dial-border rounded-xl p-5 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-indices-bright mb-4">Edit Memory</h3>

        {/* Memory Type */}
        <label className="block text-xs text-indices-muted mb-1">Type</label>
        <select
          value={memoryType}
          onChange={e => setMemoryType(e.target.value)}
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none"
        >
          {Object.entries(MEMORY_TYPES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        {/* Content */}
        <label className="block text-xs text-indices-muted mb-1">Content</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:border-lume-teal focus:outline-none resize-none"
        />

        {/* Context */}
        <label className="block text-xs text-indices-muted mb-1">Context (optional)</label>
        <input
          type="text"
          value={context}
          onChange={e => setContext(e.target.value)}
          placeholder="Why this memory matters..."
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-indices-bright placeholder-indices-recessed focus:border-lume-teal focus:outline-none"
        />

        {/* Critical toggle */}
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={isCritical}
            onChange={e => setIsCritical(e.target.checked)}
            className="accent-red-500"
          />
          <span className="text-sm text-indices-muted">Critical (safety-important, prevents decay)</span>
        </label>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-400 mb-3">{error}</p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-indices-muted hover:text-indices-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !content.trim()}
            className="px-4 py-2 text-sm bg-lume-teal text-dial-black rounded-lg font-medium hover:bg-lume-green transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MemoryCard({ memory, onEdit, onPin, onArchive, onRestore, isArchived }) {
  const typeInfo = MEMORY_TYPES[memory.memoryType] || { label: memory.memoryType, color: 'text-gray-400' }

  return (
    <Card className={`${isArchived ? 'opacity-60' : ''} hover:border-lume-teal/30 transition-colors`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Client name and type */}
          <div className="flex items-center gap-2 mb-1">
            <Link to={`/clients/${memory.clientId}`} className="font-medium text-indices-bright hover:text-lume-teal transition-colors">
              {memory.clientName}
            </Link>
            <span className={`text-xs px-1.5 py-0.5 rounded ${typeInfo.color} bg-dial-surface`}>
              {typeInfo.label}
            </span>
            {memory.isPinned && (
              <span className="text-xs text-lume-teal">Pinned</span>
            )}
            {memory.isCritical && (
              <span className="text-xs text-red-400">Critical</span>
            )}
            {isArchived && (
              <span className="text-xs text-indices-muted px-1.5 py-0.5 rounded bg-dial-elevated">Archived</span>
            )}
          </div>

          {/* Memory content */}
          <p className="text-sm text-lume-teal leading-relaxed">
            {memory.content}
          </p>

          {/* Context if present */}
          {memory.context && (
            <p className="text-xs text-indices-muted mt-1 italic">
              {memory.context}
            </p>
          )}
        </div>

        {/* Right side: timestamp, strength, actions */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1">
          <p className="text-xs text-lume-teal/50">{formatTimeAgo(memory.createdAt)}</p>
          <p className="text-xs text-indices-muted">
            {Math.round(memory.strength * 100)}%
          </p>
          {/* Action buttons */}
          <div className="flex gap-1 mt-1">
            {!isArchived && (
              <>
                <button
                  onClick={() => onEdit(memory)}
                  className="p-1 text-indices-recessed hover:text-lume-teal rounded transition-colors"
                  title="Edit"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => onPin(memory)}
                  className={`p-1 rounded transition-colors ${memory.isPinned ? 'text-lume-teal' : 'text-indices-recessed hover:text-lume-teal'}`}
                  title={memory.isPinned ? 'Unpin' : 'Pin'}
                >
                  <svg className="w-3.5 h-3.5" fill={memory.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
                <button
                  onClick={() => onArchive(memory)}
                  className="p-1 text-indices-recessed hover:text-status-error rounded transition-colors"
                  title="Archive"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </>
            )}
            {isArchived && (
              <button
                onClick={() => onRestore(memory)}
                className="p-1 text-indices-recessed hover:text-lume-teal rounded transition-colors"
                title="Restore"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}

function MemoryLogSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map(i => (
        <Card key={i}>
          <div className="animate-pulse">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-4 w-24 bg-dial-elevated rounded" />
              <div className="h-4 w-16 bg-dial-elevated rounded" />
            </div>
            <div className="h-4 w-full bg-dial-elevated rounded mb-1" />
            <div className="h-4 w-3/4 bg-dial-elevated rounded" />
          </div>
        </Card>
      ))}
    </div>
  )
}

export default function MemoryLogPage() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState('all')
  const [timeRange, setTimeRange] = useState('all')
  const [showArchived, setShowArchived] = useState(false)
  const [editingMemory, setEditingMemory] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['recentMemories', filter, timeRange, showArchived],
    queryFn: () => memories.recent({
      limit: 100,
      type: filter !== 'all' ? filter : undefined,
      days: timeRange !== 'all' ? parseInt(timeRange) : undefined,
      includeArchived: showArchived || undefined,
    }),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['recentMemories'] })
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, ...updates }) => memories.update(id, updates),
    onSuccess: invalidate,
  })

  const pinMutation = useMutation({
    mutationFn: (memory) => memories.update(memory.id, { isPinned: !memory.isPinned }),
    onSuccess: invalidate,
  })

  const archiveMutation = useMutation({
    mutationFn: (memory) => memories.delete(memory.id),
    onSuccess: invalidate,
  })

  const restoreMutation = useMutation({
    mutationFn: (memory) => memories.restore(memory.id),
    onSuccess: invalidate,
  })

  const memoryList = data?.memories || []
  const activeMemories = memoryList.filter(m => m.status !== 'archived')
  const archivedMemories = memoryList.filter(m => m.status === 'archived')

  return (
    <PageLayout>
      <PageHeader
        title="Memory Log"
        showBack
        backTo="/"
      />

      {/* Filters */}
      <div className="px-4 py-3 border-b border-dial-border bg-dial-surface">
        <div className="flex flex-wrap gap-2">
          {/* Type filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none"
          >
            <option value="all">All Types</option>
            {Object.entries(MEMORY_TYPES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Time range filter */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-1.5 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none"
          >
            <option value="all">All Time</option>
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </select>

          {/* Show archived toggle */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${
              showArchived
                ? 'border-lume-teal text-lume-teal bg-lume-teal/10'
                : 'border-dial-border text-indices-muted hover:text-lume-teal'
            }`}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>
        </div>
      </div>

      {/* Memory list */}
      <div className="px-4 py-4">
        {isLoading ? (
          <MemoryLogSkeleton />
        ) : error ? (
          <Card>
            <p className="text-red-400 text-center">Failed to load memories</p>
          </Card>
        ) : memoryList.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-dial-elevated rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <p className="text-indices-muted">No memories found</p>
              <p className="text-xs text-indices-muted mt-1">
                Memories are created during AI conversations
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {activeMemories.map(memory => (
              <MemoryCard
                key={memory.id}
                memory={memory}
                onEdit={setEditingMemory}
                onPin={m => pinMutation.mutate(m)}
                onArchive={m => {
                  if (window.confirm('Archive this memory?')) {
                    archiveMutation.mutate(m)
                  }
                }}
                onRestore={m => restoreMutation.mutate(m)}
              />
            ))}

            {/* Archived section */}
            {showArchived && archivedMemories.length > 0 && (
              <>
                <div className="flex items-center gap-2 pt-2">
                  <div className="h-px flex-1 bg-dial-border" />
                  <span className="text-xs text-indices-muted">Archived ({archivedMemories.length})</span>
                  <div className="h-px flex-1 bg-dial-border" />
                </div>
                {archivedMemories.map(memory => (
                  <MemoryCard
                    key={memory.id}
                    memory={memory}
                    isArchived
                    onEdit={setEditingMemory}
                    onPin={m => pinMutation.mutate(m)}
                    onArchive={m => archiveMutation.mutate(m)}
                    onRestore={m => restoreMutation.mutate(m)}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* Count summary */}
        {memoryList.length > 0 && (
          <p className="text-xs text-indices-muted text-center mt-4">
            Showing {activeMemories.length} {activeMemories.length === 1 ? 'memory' : 'memories'}
            {showArchived && archivedMemories.length > 0 && ` + ${archivedMemories.length} archived`}
          </p>
        )}
      </div>

      {/* Edit modal */}
      {editingMemory && (
        <EditMemoryModal
          memory={editingMemory}
          onClose={() => setEditingMemory(null)}
          onSave={(updates) => updateMutation.mutateAsync(updates)}
        />
      )}
    </PageLayout>
  )
}
